import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  FilePdf, Plus, TrashSimple, CheckCircle, Receipt, Calendar,
  Buildings, Heart, ArrowDownRight, Info, Crown, Link as LinkIcon,
  ChartLineUp, Phone, NavigationArrow, Globe,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { withUtm } from "../utils/utm";

const todayISO = () => new Date().toISOString().slice(0, 10);

const quarterLabel = (q) => `Q${q.split("-")[0].replace("Q","")} ${q.split("-")[1]}`;

const GivingLog = () => {
  const { user } = useAuth();
  const [data, setData] = useState({ logs: [], by_quarter: {}, lifetime_dollars: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState("");

  // Local Hero microsite profile
  const [profile, setProfile] = useState(null);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [trafficStats, setTrafficStats] = useState(null);
  const [profileForm, setProfileForm] = useState({
    business_name: "",
    tagline: "",
    location: "",
    website: "",
    phone: "",
    address: "",
    cta_label: "",
    custom_message: "",
    focus_areas: [],
    is_published: true,
  });

  const [form, setForm] = useState({
    business_name: "",
    business_ein: "",
    nonprofit_name: "",
    nonprofit_ein: "",
    amount: "",
    sponsorship_date: todayISO(),
    program_name: "",
    acknowledgement_received: false,
    notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        axios.get(`${API}/sponsorships/log`),
        axios.get(`${API}/local-hero/profile/mine`),
      ]);
      setData(r.data);
      // Pre-fill business name from most recent entry for convenience
      if (r.data.logs?.length && !form.business_name) {
        setForm((f) => ({
          ...f,
          business_name: r.data.logs[0].business_name || "",
          business_ein: r.data.logs[0].business_ein || "",
        }));
      }
      // Hydrate profile state
      if (p.data && p.data.slug) {
        setProfile(p.data);
        // Fetch 30-day traffic report alongside profile (silent if it fails)
        axios.get(`${API}/local-hero/clicks/mine?days=30`)
          .then(res => setTrafficStats(res.data))
          .catch(() => {});
        setProfileForm({
          business_name: p.data.business_name || "",
          tagline: p.data.tagline || "",
          location: p.data.location || "",
          website: p.data.website || "",
          phone: p.data.phone || "",
          address: p.data.address || "",
          cta_label: p.data.cta_label || "",
          custom_message: p.data.custom_message || "",
          focus_areas: p.data.focus_areas || [],
          is_published: p.data.is_published !== false,
        });
      } else if (r.data.logs?.length) {
        setProfileForm((f) => ({ ...f, business_name: r.data.logs[0].business_name || "" }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load giving log");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/sponsorships/log`, {
        ...form,
        amount: parseInt(form.amount || "0", 10),
      });
      toast.success("Sponsorship logged");
      setShowAdd(false);
      setForm({
        ...form,
        nonprofit_name: "",
        nonprofit_ein: "",
        amount: "",
        program_name: "",
        notes: "",
        acknowledgement_received: false,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry? It will be removed from future Impact PDFs.")) return;
    try {
      await axios.delete(`${API}/sponsorships/log/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    }
  };

  const saveProfile = async () => {
    if (!profileForm.business_name?.trim() || profileForm.business_name.trim().length < 2) {
      toast.error("Business name is required");
      return;
    }
    setProfileSaving(true);
    try {
      const r = await axios.post(`${API}/local-hero/profile`, profileForm);
      setProfile(r.data);
      setShowProfileEditor(false);
      toast.success(profile ? "Microsite updated" : "Microsite published");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setProfileSaving(false); }
  };

  const copyMicrositeUrl = async () => {
    if (!profile?.slug) return;
    // Tag the owner's "Copy my page URL" so we attribute traffic they drive themselves.
    const url = withUtm(`${window.location.origin}/local-hero/${profile.slug}`, "local_hero_owner", "link", `local_hero_${profile.slug}`);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Microsite URL copied");
    } catch { toast.error("Couldn't copy"); }
  };

  const downloadPdf = async (year, quarter) => {
    const key = `${year}-${quarter}`;
    setPdfLoading(key);
    try {
      const res = await axios.get(`${API}/sponsorships/impact-pdf?year=${year}&quarter=${quarter}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `FlashGrants_Impact_Q${quarter}_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Q${quarter} ${year} Impact PDF downloaded`);
    } catch {
      toast.error("Couldn't generate PDF");
    } finally { setPdfLoading(""); }
  };

  // Build quarter cards — sort latest first
  const sortedQuarters = Object.entries(data.by_quarter || {}).sort((a, b) => {
    const [aq, ay] = [parseInt(a[0].split("-")[0].replace("Q",""),10), parseInt(a[0].split("-")[1],10)];
    const [bq, by] = [parseInt(b[0].split("-")[0].replace("Q",""),10), parseInt(b[0].split("-")[1],10)];
    return (by * 10 + bq) - (ay * 10 + aq);
  });

  // Suggest the current quarter as a card even if no entries yet
  const now = new Date();
  const currQuarter = Math.floor(now.getUTCMonth() / 3) + 1;
  const currYear = now.getUTCFullYear();
  const currKey = `Q${currQuarter}-${currYear}`;
  const hasCurrent = sortedQuarters.some(([k]) => k === currKey);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto py-6" data-testid="giving-log-page">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
              <Receipt weight="fill" className="w-7 h-7 text-emerald-400" />
              Giving Log & Impact PDFs
            </h1>
            <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
              Log every sponsorship you pay out. Download audit-ready quarterly Impact PDFs for franchisor HQ, your accountant, or your board.
            </p>
          </div>
          <Button
            onClick={() => setShowAdd(true)}
            className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
            data-testid="add-log-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Log Sponsorship
          </Button>
        </div>

        {/* Lifetime summary strip */}
        <div className="grid sm:grid-cols-3 gap-3" data-testid="lifetime-stats">
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="py-4">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Lifetime giving</p>
              <p className="text-2xl font-black text-emerald-400 mt-1">${(data.lifetime_dollars || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="py-4">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Logged entries</p>
              <p className="text-2xl font-black text-zinc-100 mt-1">{data.total || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="py-4">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Nonprofits supported</p>
              <p className="text-2xl font-black text-zinc-100 mt-1">
                {new Set((data.logs || []).map((l) => (l.nonprofit_name || "").toLowerCase())).size}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Local Hero microsite card */}
        <Card className="bg-zinc-950 border-emerald-500/30 border" data-testid="microsite-card">
          <CardContent className="py-5">
            {profile?.slug ? (
              <div className="flex flex-wrap items-start gap-4 justify-between">
                <div className="flex-1 min-w-[260px]">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-mono mb-1 flex items-center gap-1.5">
                    <Crown weight="fill" className="w-3.5 h-3.5" /> Your Local Hero microsite
                  </p>
                  <p className="text-lg font-bold text-zinc-100">{profile.business_name}</p>
                  <p className="text-xs text-zinc-500 mt-0.5 font-mono break-all">
                    flash-grants.com/local-hero/{profile.slug}
                    {!profile.is_published && <span className="text-amber-400 ml-2">· UNPUBLISHED</span>}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a href={`/local-hero/${profile.slug}`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-sm" data-testid="view-microsite">
                      View live
                    </Button>
                  </a>
                  <Button onClick={copyMicrositeUrl} variant="outline" size="sm" className="border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-sm" data-testid="copy-microsite-url">
                    <LinkIcon className="w-4 h-4 mr-1" /> Copy URL
                  </Button>
                  <Button onClick={() => setShowProfileEditor(true)} size="sm" className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="edit-microsite">
                    Edit
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-4 justify-between">
                <div className="flex-1 min-w-[260px]">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-mono mb-1 flex items-center gap-1.5">
                    <Crown weight="fill" className="w-3.5 h-3.5" /> Publish your Local Hero microsite
                  </p>
                  <p className="text-sm text-zinc-200">
                    Get a free public page showcasing your sponsorships — your branded URL, embeddable badge, and a leaderboard spot among SC Local Heroes.
                  </p>
                </div>
                <Button onClick={() => setShowProfileEditor(true)} className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="create-microsite">
                  <Plus className="w-4 h-4 mr-1" /> Create microsite
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Local Hero Traffic Report — Phase 2: shows the sponsor their click ROI */}
        {profile?.slug && trafficStats && (
          <Card className="bg-zinc-950 border-zinc-800" data-testid="traffic-report-card">
            <CardContent className="py-5">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-mono mb-1 flex items-center gap-1.5">
                    <ChartLineUp weight="fill" className="w-3.5 h-3.5" /> Your traffic report
                  </p>
                  <p className="text-sm text-zinc-300">Outbound clicks from your microsite — last 30 days</p>
                </div>
                <p className="text-xs text-zinc-500">
                  Monthly report emailed on the 1st
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-sm p-3" data-testid="traffic-total">
                  <p className="text-[10px] uppercase tracking-widest text-emerald-300/80 font-mono">Total clicks</p>
                  <p className="text-3xl font-black text-emerald-400 mt-1">{(trafficStats.total_clicks || 0).toLocaleString()}</p>
                </div>
                <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-3" data-testid="traffic-website">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-1"><Globe weight="fill" className="w-3 h-3" /> Website</p>
                  <p className="text-2xl font-bold text-zinc-100 mt-1">{(trafficStats.by_type?.website || 0).toLocaleString()}</p>
                </div>
                <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-3" data-testid="traffic-phone">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-1"><Phone weight="fill" className="w-3 h-3" /> Calls</p>
                  <p className="text-2xl font-bold text-zinc-100 mt-1">{(trafficStats.by_type?.phone || 0).toLocaleString()}</p>
                </div>
                <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-3" data-testid="traffic-directions">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-1"><NavigationArrow weight="fill" className="w-3 h-3" /> Directions</p>
                  <p className="text-2xl font-bold text-zinc-100 mt-1">{(trafficStats.by_type?.directions || 0).toLocaleString()}</p>
                </div>
              </div>

              {(trafficStats.total_clicks || 0) === 0 ? (
                <p className="text-xs text-zinc-500 mt-4 italic">
                  No clicks yet — your microsite hasn't driven traffic in the last 30 days. Share your microsite URL on your website, email signature, and social to start.
                </p>
              ) : (
                <p className="text-xs text-zinc-500 mt-4">
                  💡 {trafficStats.total_clicks} {trafficStats.total_clicks === 1 ? "person took action" : "people took action"} from your Local Hero microsite this month — that's measurable traffic your sponsorship drove to your business.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quarterly PDF cards */}
        <div>
          <h2 className="text-sm uppercase tracking-widest text-zinc-500 font-mono mb-3">Quarterly Impact PDFs</h2>
          {loading ? (
            <div className="py-12 text-center text-zinc-500">Loading…</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="quarter-cards">
              {/* Always show the current quarter even if empty — encourages new users to log their first entry */}
              {!hasCurrent && (
                <Card className="bg-zinc-950 border-emerald-500/30 border-dashed" data-testid={`quarter-${currKey}`}>
                  <CardContent className="py-5 flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-emerald-400 font-mono mb-1">Current quarter</p>
                      <p className="text-lg font-bold text-zinc-100">{quarterLabel(currKey)}</p>
                      <p className="text-xs text-zinc-500 mt-1">No entries yet — log your first sponsorship to populate.</p>
                    </div>
                    <Button
                      onClick={() => downloadPdf(currYear, currQuarter)}
                      disabled={pdfLoading === `${currYear}-${currQuarter}`}
                      variant="outline"
                      size="sm"
                      className="border-emerald-700/50 text-emerald-300 hover:bg-emerald-500/10 rounded-sm whitespace-nowrap"
                      data-testid={`pdf-${currKey}`}
                    >
                      <FilePdf className="w-4 h-4 mr-1" /> {pdfLoading === `${currYear}-${currQuarter}` ? "..." : "PDF"}
                    </Button>
                  </CardContent>
                </Card>
              )}
              {sortedQuarters.map(([key, bucket]) => {
                const [qStr, yStr] = key.split("-");
                const q = parseInt(qStr.replace("Q",""), 10);
                const y = parseInt(yStr, 10);
                const loadingKey = `${y}-${q}`;
                return (
                  <Card key={key} className="bg-zinc-950 border-zinc-800" data-testid={`quarter-${key}`}>
                    <CardContent className="py-5 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-zinc-500 font-mono mb-1">{quarterLabel(key)}</p>
                        <p className="text-2xl font-black text-emerald-400">${bucket.total_dollars.toLocaleString()}</p>
                        <p className="text-xs text-zinc-500 mt-1">
                          {bucket.count} {bucket.count === 1 ? "entry" : "entries"} · {bucket.acknowledged} acknowledged
                        </p>
                      </div>
                      <Button
                        onClick={() => downloadPdf(y, q)}
                        disabled={pdfLoading === loadingKey}
                        size="sm"
                        className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm whitespace-nowrap"
                        data-testid={`pdf-${key}`}
                      >
                        <FilePdf className="w-4 h-4 mr-1" /> {pdfLoading === loadingKey ? "..." : "Download PDF"}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Compliance hint */}
        <Card className="bg-emerald-500/5 border-emerald-500/30">
          <CardContent className="py-4 flex items-start gap-3">
            <Info weight="fill" className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-zinc-200 font-semibold">Audit-ready format for franchisor HQ.</p>
              <p className="text-xs text-zinc-400 leading-relaxed mt-1">
                Each Impact PDF is letter-size with your business name + EIN, the reporting quarter, total contributions, nonprofits supported, an acknowledgement-status column, and an attestation paragraph. Retain the recipient's acknowledgement letters alongside the PDF for full audit defensibility.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Full log table */}
        <div>
          <h2 className="text-sm uppercase tracking-widest text-zinc-500 font-mono mb-3">All entries</h2>
          {loading ? null : data.logs.length === 0 ? (
            <Card className="bg-zinc-950 border-zinc-800">
              <CardContent className="py-12 text-center">
                <Heart className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">No sponsorships logged yet. Click <strong className="text-emerald-400">Log Sponsorship</strong> to record your first.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-zinc-950 border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="log-table">
                  <thead className="bg-zinc-900 text-zinc-500 uppercase text-[10px] tracking-wider font-mono">
                    <tr>
                      <th className="text-left px-4 py-3">Date</th>
                      <th className="text-left px-4 py-3">Nonprofit</th>
                      <th className="text-left px-4 py-3 hidden sm:table-cell">Program</th>
                      <th className="text-right px-4 py-3">Amount</th>
                      <th className="text-center px-4 py-3 hidden md:table-cell">Ack.</th>
                      <th className="text-right px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.logs.map((l) => (
                      <tr key={l.log_id} className="border-t border-zinc-900 hover:bg-zinc-900/50" data-testid={`log-row-${l.log_id}`}>
                        <td className="px-4 py-3 text-zinc-400 font-mono text-xs whitespace-nowrap">{l.sponsorship_date}</td>
                        <td className="px-4 py-3 text-zinc-100">
                          {l.nonprofit_name}
                          {l.nonprofit_ein && <span className="text-[10px] text-zinc-500 ml-1 font-mono">· EIN {l.nonprofit_ein}</span>}
                        </td>
                        <td className="px-4 py-3 text-zinc-400 hidden sm:table-cell text-xs">{l.program_name || "—"}</td>
                        <td className="px-4 py-3 text-right text-emerald-400 font-bold">${l.amount.toLocaleString()}</td>
                        <td className="px-4 py-3 text-center hidden md:table-cell">
                          {l.acknowledgement_received ? (
                            <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 inline" />
                          ) : (
                            <span className="text-[10px] text-zinc-500">Pending</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(l.log_id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm h-7 px-2"
                            data-testid={`delete-log-${l.log_id}`}
                          >
                            <TrashSimple className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>

        {/* Add log modal */}
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]">
            <DialogHeader>
              <DialogTitle>Log a Sponsorship</DialogTitle>
              <p className="text-xs text-zinc-500 mt-1">Record a sponsorship your business has paid out. This will appear in the appropriate quarterly Impact PDF.</p>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Your business name</Label>
                  <Input value={form.business_name} onChange={(e) => setForm({...form, business_name: e.target.value})} className="bg-zinc-900 border-zinc-800" data-testid="form-business-name" /></div>
                <div><Label className="text-xs">Business EIN <span className="text-zinc-600">(optional)</span></Label>
                  <Input value={form.business_ein} onChange={(e) => setForm({...form, business_ein: e.target.value})} placeholder="82-1234567" className="bg-zinc-900 border-zinc-800" data-testid="form-business-ein" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Recipient nonprofit</Label>
                  <Input value={form.nonprofit_name} onChange={(e) => setForm({...form, nonprofit_name: e.target.value})} placeholder="Lowcountry Mentors" className="bg-zinc-900 border-zinc-800" data-testid="form-np-name" /></div>
                <div><Label className="text-xs">Nonprofit EIN <span className="text-zinc-600">(optional)</span></Label>
                  <Input value={form.nonprofit_ein} onChange={(e) => setForm({...form, nonprofit_ein: e.target.value})} placeholder="57-7654321" className="bg-zinc-900 border-zinc-800" data-testid="form-np-ein" /></div>
              </div>
              <div><Label className="text-xs">Program or event name <span className="text-zinc-600">(optional)</span></Label>
                <Input value={form.program_name} onChange={(e) => setForm({...form, program_name: e.target.value})} placeholder="Annual Spring Gala" className="bg-zinc-900 border-zinc-800" data-testid="form-program" /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Amount ($)</Label>
                  <Input type="number" min="1" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} placeholder="1000" className="bg-zinc-900 border-zinc-800" data-testid="form-amount" /></div>
                <div><Label className="text-xs">Date paid</Label>
                  <Input type="date" value={form.sponsorship_date} onChange={(e) => setForm({...form, sponsorship_date: e.target.value})} className="bg-zinc-900 border-zinc-800" data-testid="form-date" /></div>
              </div>
              <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                <input type="checkbox" checked={form.acknowledgement_received} onChange={(e) => setForm({...form, acknowledgement_received: e.target.checked})} className="rounded-sm" data-testid="form-ack" />
                I've received an acknowledgement letter from this nonprofit
              </label>
              <div><Label className="text-xs">Notes <span className="text-zinc-600">(optional, 500 chars)</span></Label>
                <Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value.slice(0, 500)})} rows={2} className="bg-zinc-900 border-zinc-800 text-sm" placeholder="Logo placement, mentions in newsletter, etc." data-testid="form-notes" /></div>
              <Button onClick={save} disabled={saving} className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="form-submit">
                {saving ? "Saving…" : "Log Sponsorship"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Microsite profile editor */}
        <Dialog open={showProfileEditor} onOpenChange={setShowProfileEditor}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Crown weight="fill" className="w-5 h-5 text-emerald-400" />
                {profile ? "Edit your microsite" : "Publish your Local Hero microsite"}
              </DialogTitle>
              <p className="text-xs text-zinc-500 mt-1">
                Public page showcasing your sponsorships. Auto-updates as you log new contributions.
              </p>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div><Label className="text-xs">Business name</Label>
                <Input value={profileForm.business_name} onChange={(e) => setProfileForm({...profileForm, business_name: e.target.value})} className="bg-zinc-900 border-zinc-800" data-testid="profile-business-name" /></div>
              <div><Label className="text-xs">Tagline <span className="text-zinc-600">(optional, one line)</span></Label>
                <Input value={profileForm.tagline} onChange={(e) => setProfileForm({...profileForm, tagline: e.target.value.slice(0, 140)})} placeholder="Proudly serving the SC community since 1998" className="bg-zinc-900 border-zinc-800" data-testid="profile-tagline" /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Location <span className="text-zinc-600">(city, SC)</span></Label>
                  <Input value={profileForm.location} onChange={(e) => setProfileForm({...profileForm, location: e.target.value})} placeholder="Myrtle Beach, SC" className="bg-zinc-900 border-zinc-800" data-testid="profile-location" /></div>
                <div><Label className="text-xs">Website <span className="text-zinc-600">(optional)</span></Label>
                  <Input value={profileForm.website} onChange={(e) => setProfileForm({...profileForm, website: e.target.value})} placeholder="yourcompany.com" className="bg-zinc-900 border-zinc-800" data-testid="profile-website" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Phone <span className="text-zinc-600">(optional, tap-to-call)</span></Label>
                  <Input value={profileForm.phone} onChange={(e) => setProfileForm({...profileForm, phone: e.target.value.slice(0, 32)})} placeholder="(843) 555-0199" className="bg-zinc-900 border-zinc-800" data-testid="profile-phone" /></div>
                <div><Label className="text-xs">Custom CTA label <span className="text-zinc-600">(optional)</span></Label>
                  <Input value={profileForm.cta_label} onChange={(e) => setProfileForm({...profileForm, cta_label: e.target.value.slice(0, 40)})} placeholder="Order Online · Book a Service · Visit Store" className="bg-zinc-900 border-zinc-800" data-testid="profile-cta-label" /></div>
              </div>
              <div><Label className="text-xs">Street address <span className="text-zinc-600">(optional, opens Google Maps)</span></Label>
                <Input value={profileForm.address} onChange={(e) => setProfileForm({...profileForm, address: e.target.value.slice(0, 200)})} placeholder="123 Main St, Myrtle Beach, SC 29577" className="bg-zinc-900 border-zinc-800" data-testid="profile-address" /></div>
              <div><Label className="text-xs">Causes you champion <span className="text-zinc-600">(comma-separated, up to 6)</span></Label>
                <Input
                  value={profileForm.focus_areas.join(", ")}
                  onChange={(e) => setProfileForm({ ...profileForm, focus_areas: e.target.value.split(",").map(s => s.trim()).filter(Boolean).slice(0, 6) })}
                  placeholder="Youth & Education, Food Security, Faith-Based"
                  className="bg-zinc-900 border-zinc-800" data-testid="profile-focus-areas" /></div>
              <div><Label className="text-xs">Personal message <span className="text-zinc-600">(optional, 600 chars)</span></Label>
                <Textarea value={profileForm.custom_message} onChange={(e) => setProfileForm({...profileForm, custom_message: e.target.value.slice(0, 600)})} rows={3} placeholder="Why your business gives back to the community..." className="bg-zinc-900 border-zinc-800 text-sm" data-testid="profile-custom-message" /></div>
              <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                <input type="checkbox" checked={profileForm.is_published} onChange={(e) => setProfileForm({...profileForm, is_published: e.target.checked})} className="rounded-sm" data-testid="profile-published" />
                Publish publicly (uncheck to hide from search + leaderboard)
              </label>
              <Button onClick={saveProfile} disabled={profileSaving} className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="profile-submit">
                {profileSaving ? "Saving…" : (profile ? "Save changes" : "Publish microsite")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default GivingLog;
